import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SuperAdminSidebar from '../../../components/SuperAdminsidebar';
import { fetchProformaDetails } from '../../../../services/superadmin_services/proforma_services';
import { downloadProformaAttachment, triggerDownload } from '../../../../services/superadmin_services/download_attachment_service';

const ProformaDetailsPage = () => {
  const { proformaId } = useParams();
  const [proforma, setProforma] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingIds, setDownloadingIds] = useState(new Set());
  const [downloadError, setDownloadError] = useState('');

  // Load proforma details
  const loadProformaDetails = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetchProformaDetails(proformaId);
      setProforma(response.data.proforma);
      setAttachments(response.data.attachments || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle attachment download
  const handleDownload = async (attachmentId, title) => {
    try {
      setDownloadingIds(prev => new Set([...prev, attachmentId]));
      setDownloadError('');
      
      const response = await downloadProformaAttachment(attachmentId);
      triggerDownload(response.blob, response.filename || title);
    } catch (err) {
      console.error('Download error:', err);
      setDownloadError(`Failed to download "${title}": ${err.message}`);
    } finally {
      setDownloadingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(attachmentId);
        return newSet;
      });
    }
  };

  // Format currency in XAF (Cameroon Francs)
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-CM', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-orange-100 text-orange-800';
      case 'converted':
        return 'bg-secondary-100 text-secondary-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  useEffect(() => {
    if (proformaId) {
      loadProformaDetails();
    }
  }, [proformaId]);

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <SuperAdminSidebar />
        <div className="flex-1 ml-64 p-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
              <p className="mt-2 text-gray-600">Loading proforma details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <SuperAdminSidebar />
        <div className="flex-1 ml-64 p-8">
          <div className="max-w-6xl mx-auto">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
              {error}
            </div>
            <div className="mt-4">
              <Link
                to="/superadmin/proformas"
                className="text-secondary-600 hover:text-secondary-800"
              >
                ← Back to Proformas
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <SuperAdminSidebar />
      
      <div className="flex-1 ml-64 p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Link
              to="/superadmin/proformas"
              className="text-secondary-600 hover:text-secondary-800 mb-4 inline-block"
            >
              ← Back to Proformas
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">Proforma Details</h1>
          </div>

          {/* Download Error Message */}
          {downloadError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
              {downloadError}
              <button
                onClick={() => setDownloadError('')}
                className="ml-2 text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Proforma Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <p className="text-gray-900">{proforma?.title}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Proforma Number</label>
                    <p className="text-gray-900">{proforma?.proforma_number || '-'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                    <p className="text-gray-900 font-semibold text-lg">
                      {formatCurrency(proforma?.amount)}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(proforma?.status)}`}>
                      {proforma?.status_display}
                    </span>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                    <p className="text-gray-900">{proforma?.client || '-'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client Email</label>
                    <p className="text-gray-900">{proforma?.client_email || '-'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
                    <p className="text-gray-900">{formatDate(proforma?.issue_date)}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
                    <p className="text-gray-900">{formatDate(proforma?.expiry_date)}</p>
                  </div>
                </div>
                
                {proforma?.description && (
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <p className="text-gray-900">{proforma.description}</p>
                  </div>
                )}
              </div>

              {/* Attachments */}
              {attachments.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Attachments ({attachments.length})
                  </h2>
                  
                  <div className="space-y-3">
                    {attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center justify-between p-3 border border-gray-200 rounded-md hover:bg-gray-50"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{attachment.title}</p>
                          <p className="text-sm text-gray-500">
                            {attachment.file_type?.toUpperCase()} • {formatFileSize(attachment.file_size)} • 
                            Uploaded {formatDate(attachment.upload_date)}
                          </p>
                        </div>
                        
                        <button
                          onClick={() => handleDownload(attachment.id, attachment.title)}
                          disabled={downloadingIds.has(attachment.id)}
                          className="ml-4 px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-secondary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {downloadingIds.has(attachment.id) ? (
                            <span className="flex items-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Downloading...
                            </span>
                          ) : (
                            'Download'
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar - Owner Information */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Owner Information</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
                    <p className="text-gray-900">
                      {proforma?.owner_details?.first_name} {proforma?.owner_details?.last_name}
                    </p>
                    <p className="text-sm text-gray-500">@{proforma?.owner_details?.username}</p>
                    <p className="text-sm text-gray-500">{proforma?.owner_details?.email}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Created By</label>
                    <p className="text-gray-900">
                      {proforma?.created_by_details?.first_name} {proforma?.created_by_details?.last_name}
                    </p>
                    <p className="text-sm text-gray-500">@{proforma?.created_by_details?.username}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date Created</label>
                    <p className="text-gray-900">{formatDate(proforma?.date_created)}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Modified</label>
                    <p className="text-gray-900">{formatDate(proforma?.date_modified)}</p>
                    {proforma?.last_modified_by_details && (
                      <p className="text-sm text-gray-500">
                        by {proforma.last_modified_by_details.first_name} {proforma.last_modified_by_details.last_name}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProformaDetailsPage;